import {TranslationObject} from '@colonist/i18n'
import {UIBaseShareImage} from '../ui_share_image/UIBaseShareImage'
import {ViewPopup} from './ViewPopup'
import {createExternalImgIn, createParagraphIn, setElementInnerHTML, setElementOnClick} from '../ui_utils/DomUtils'
import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {AnalyticEventCategory, AnalyticEventType, AnalyticEventValue} from '../../../../shared/entity_datas/EntityDatas'
import {CA} from '../analytics/Analytics'
import {UIEndGameTitleImage} from '../ui_share_image/UIEndGameTitleImage'
import {ImageURL} from '@colonist/types'

export class ViewPopupShareImage extends ViewPopup {
    private readonly currentImage: UIBaseShareImage
    private readonly otherImage?: UIBaseShareImage | undefined
    private readonly showingTitleImage: boolean
    private shareAction: (shareImage: UIBaseShareImage) => Promise<void>

    constructor(currentImage: UIBaseShareImage, otherImage?: UIBaseShareImage, showingTitleImage: boolean = false) {
        const title: TranslationObject = {key: 'strings:game.endGame.shareImagePopup.title'}
        const body: TranslationObject = {key: 'strings:colonistCommon.utils.emptyString'}

        super(title, body, () => undefined, () => undefined, true)

        this.currentImage = currentImage
        this.otherImage = otherImage
        this.showingTitleImage = showingTitleImage

        this.contentContainer.classList.add('popup-share-image')
        this.changeCheckButtonText({key: 'strings:game.endGame.shareImagePopup.copy'})
        this.changeXButtonText({key: 'strings:game.endGame.shareImagePopup.download'})

        setElementInnerHTML(this.body, {key: 'strings:colonistCommon.utils.emptyString'})
        void this.loadImage()

        setElementOnClick(this.checkButton, async() => {
            await this.copyFileToClipboard()
            this.changeCheckButtonText({key: 'strings:game.endGame.shareImagePopup.copied'})
            setTimeout(() => {
                this.changeCheckButtonText({key: 'strings:game.endGame.shareImagePopup.copy'})
            }, 1500)
            void ViewPopupShareImage.sendCopyButtonClickAnalytic()
            this.sendShareTitleAnalytic()
        })

        setElementOnClick(this.xButton, () => {
            void this.downloadFile()
            this.removePopup()
            void ViewPopupShareImage.sendDownloadButtonClickAnalytic()
            this.sendShareTitleAnalytic()
        })
    }

    setShareAction(shareAction: (shareImage: UIBaseShareImage) => Promise<void>) {
        this.shareAction = shareAction
        this.removeXButton()
        this.changeCheckButtonText({key: 'strings:game.endGame.cornerButton.share'})
        setElementOnClick(this.checkButton, async() => {
            await this.shareAction(this.currentImage)
            this.sendShareTitleAnalytic()
        })
    }

    private async loadImage() {
        const imageFile = await this.currentImage.getImageFile()
        if(imageFile == undefined) return

        const url = URL.createObjectURL(imageFile) as ImageURL

        createExternalImgIn(this.body, url, {key: 'strings:game.endGame.shareImagePopup.title'})

        if(this.otherImage != undefined) this.createSwitchImageText()

        this.show()
    }

    private createSwitchImageText() {
        const text: TranslationObject = this.showingTitleImage ? {key: 'strings:game.endGame.shareImagePopup.viewResults'} : {key: 'strings:game.endGame.shareImagePopup.viewTitle'}

        const switchImageText = createParagraphIn(this.body, text, 'switch-image-text')
        setElementOnClick(switchImageText, () => this.displayOtherImage())
    }

    private displayOtherImage() {
        if(this.otherImage == undefined) return

        const otherPopup = new ViewPopupShareImage(this.otherImage, this.currentImage, !this.showingTitleImage)
        if(this.shareAction != undefined) otherPopup.setShareAction(this.shareAction)

        this.removePopup()
    }

    private async copyFileToClipboard() {
        const imageFile = await this.currentImage.getImageFile()
        if(imageFile == undefined) return

        try {
            await navigator.clipboard.write([
                new ClipboardItem({
                    [imageFile.type]: imageFile,
                }),
            ])
        } catch(error) {
            // eslint-disable-next-line no-console
            console.error('Error copying file to clipboard. Clipboard Permissions likely missing', error)
            this.changeCheckButtonText({key: 'strings:game.endGame.shareImagePopup.cantCopy'})
        }
    }

    private async downloadFile() {
        const imageFile = await this.currentImage.getImageFile()
        if(imageFile == undefined) return

        const url = URL.createObjectURL(imageFile)

        const a = document.createElement('a')
        a.href = url
        a.download = imageFile.name

        document.body.appendChild(a)

        a.click()

        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    private static sendCopyButtonClickAnalytic() {
        const copyButtonClickEvent: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.EndGameShareButton,
            category: AnalyticEventCategory.Virality,
            floatValue: AnalyticEventValue.EndGameShareCopyButton,
        }
        void CA.sendAnalyticEventOnce(copyButtonClickEvent)
    }

    private static sendDownloadButtonClickAnalytic() {
        const downloadButtonClickEvent: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.EndGameShareButton,
            category: AnalyticEventCategory.Virality,
            floatValue: AnalyticEventValue.EndGameShareDownloadButton,
        }
        void CA.sendAnalyticEventOnce(downloadButtonClickEvent)
    }

    private sendShareTitleAnalytic() {
        if(this.currentImage instanceof UIEndGameTitleImage == false) return
        const titleEnum = (this.currentImage as UIEndGameTitleImage).title

        const shareTitleEvent: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.EndGameShareTitle,
            category: AnalyticEventCategory.Virality,
            floatValue: titleEnum,
        }
        void CA.sendAnalyticEventOnce(shareTitleEvent)
    }
}
